import React, { Suspense } from 'react'
import { lazy } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import MainLayout from 'layouts/MainLayout'
import AuthLayout from 'layouts/AuthLayout'
import ProfileLayout from 'layouts/ProfileLayout'
import BuySellLayout from 'layouts/BuySellLayout'
import ProtectedBasicLayout from 'layouts/ProtectedBasicLayout'
import AuthRoute from './authRoute'
import ProtectedRoute from './protectedRoute'
import ErrorBoundary from './errorBoundary'
import { Spin } from 'antd'
import BlankLayout from 'layouts/BlankLayout'
import Sponsorship from 'pages/main/profile/Sponsorship'
import Transaction from 'pages/main/profile/subpages/transaction/index.js'
import TransactionRoute from './transactionRoute'
// Error
const NotFound = lazy(() => import('pages/error/404.js'))
// Auth
const LoginPage = lazy(() => import('pages/auth/login/index.js'))
const RegisterPage = lazy(() => import('pages/auth/register/index.js'))
const ConfirmEmailRegisterPage = lazy(() =>
  import('pages/auth/register/ConfirmEmailRegisterPage.js')
)
const ForgotPasswordPage = lazy(() => import('pages/auth/forgot-password/index.js'))
const ResetPasswordPage = lazy(() => import('pages/auth/reset-password/index.js'))

// Main - Home
const HomePage = lazy(() => import('pages/main/home/index.js'))

// Main - buyer
const WantedCompaniesPage = lazy(() => import('pages/main/buyer/index.js'))
const BuyCompanyPage = lazy(() => import('pages/main/buyer/BuyCompanyPage.js'))
const BuyCompanyV2Page = lazy(() => import('pages/main/buyer/BuyCompanyPageV2'))
const CompanyForSaleDetailPage = lazy(() => import('pages/main/buyer/CompanyForSaleDetailPage.js'))

// Main - seller
const CompanyForSalePage = lazy(() => import('pages/main/seller/index.js'))
// const SellCompanyPageLegacy = lazy(() => import('pages/main/seller/SellCompanyPageLegacy.js'))
const SellCompanyPage = lazy(() => import('pages/main/seller/SellCompanyPage.js'))
const WantedCompanyDetailPage = lazy(() => import('pages/main/seller/WantedCompanyDetailPage.js'))

// Main - Advisor
const AdvisorCatalogPage = lazy(() => import('pages/main/advisor/index.js'))
const AdvisorDetailPage = lazy(() => import('pages/main/advisor/AdvisorDetailPage.js'))
const CreateAdvisorProfilePage = lazy(() =>
  import('pages/main/advisor/CreateAdvisorProfilePage.js')
)
const CreateAdvisorProfilePageV2 = lazy(() =>
  import('pages/main/advisor/CreateAdvisorProfilePageV2')
)

// Main - Profile
const ProfilePage = lazy(() => import('pages/main/profile/index.js'))
const TransactionPage = lazy(() => import('pages/main/profile/subpages/transaction/index.js'))
const DetailTransactionPage = lazy(() => import('pages/main/profile/subpages/transaction/Detail'))
const TransactionInvoicePage = lazy(() =>
  import('pages/main/profile/subpages/transaction/TransactionInvoicePage')
)
const MembershipPage = lazy(() => import('pages/main/profile/subpages/membership'))
const FavoritePage = lazy(() => import('pages/main/profile/subpages/favorite'))
const SearchAgentPage = lazy(() => import('pages/main/profile/subpages/search-agent'))
const MessagePage = lazy(() => import('pages/main/profile/subpages/message'))
const MessageRequestPage = lazy(() => import('pages/main/profile/subpages/message/MessageRequest'))
const ProfileEvent = lazy(() => import('pages/main/profile/subpages/event'))
const ProfileEventDetail = lazy(() => import('pages/main/profile/subpages/event/Detail'))
const ProfileWebinar = lazy(() => import('pages/main/profile/subpages/webinars'))
const ProfileWebinarDetail = lazy(() =>
  import('pages/main/profile/subpages/webinars/DetailWebinar')
)

// Main - Pricing
const PricingPage = lazy(() => import('pages/main/pricing/index.js'))

// Main - Valuation
// const ValuationPage = lazy(() => import('pages/main/valuation/index.js')) Phase 2

// Main - Blog
const BlogPage = lazy(() => import('pages/main/more/blog/'))
const BlogDetailPage = lazy(() => import('pages/main/more/blog/BlogDetailPage'))

// Main - Knowledge Base
const KnowledgeBasegPage = lazy(() => import('pages/main/more/knowledgeBase'))
// Main - Detail Knowledge Base
const DetailKnowledgeBasegPage = lazy(() => import('pages/main/more/knowledgeBase/Detail'))

// Main - About Us
const AboutUsPage = lazy(() => import('pages/main/more/aboutUs/index'))

// Main - Support
const SupportPage = lazy(() => import('pages/main/more/support/index'))

// Main - Event
const EventPage = lazy(() => import('pages/main/more/event/index'))
const EventDetailPage = lazy(() => import('pages/main/more/event/EventDetail'))
const EventCheckoutPage = lazy(() => import('pages/main/more/event/EventCheckout'))
const CheckoutExtraBundle = lazy(() => import('pages/main/checkout/CheckoutExtraBundle'))
const CreditCardPaymentPage = lazy(() => import('pages/main/checkout/CreditCardPaymentPage'))

// Dynamic Page
const ListingVerificationPage = lazy(() => import('pages/main/ListingVerificationPage.js'))

const OnboardingPage = lazy(() => import('pages/main/onboarding/index.js'))
const SubscriptionFormPage = lazy(() => import('pages/main/subscription/index.js'))
const SubscriptionPaymentSuccessPage = lazy(() =>
  import('pages/main/subscription/SubscriptionPaymentSuccessPage.js')
)
const PackagingPage = lazy(() => import('pages/main/packaging/index'))
const PackagingPaymentSuccess = lazy(() =>
  import('pages/main/packaging/PackagingPaymentSuccessPage.js')
)
const UpgradeMembershipPage = lazy(() => import('pages/main/profile/subpages/membership/Upgrade'))
const RenewMembershipPage = lazy(() => import('pages/main/profile/subpages/membership/Renew'))
const XenditTransactionPage = lazy(() =>
  import('pages/main/profile/subpages/transaction/DetailTransaction')
)
const ValuationToolPage = lazy(() => import('pages/main/valuation-tool/calculateTools'))
const LadingPageValuationTool = lazy(() => import('pages/main/valuation-tool'))
const ValuationToolsCheckout = lazy(() =>
  import('pages/main/valuation-tool/ValuationToolsCheckout')
)

const router = createBrowserRouter([
  // AUTH
  {
    errorElement: <ErrorBoundary />,
    element: (
      <AuthRoute>
        <AuthLayout />
      </AuthRoute>
    ),
    children: [
      {
        path: '/login',
        element: <LoginPage />
      },
      {
        path: '/register',
        element: <RegisterPage />
      },
      {
        path: '/confirm-email',
        element: <ConfirmEmailRegisterPage />
      },
      {
        path: '/verify-account',
        element: <ConfirmEmailRegisterPage />
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />
      },
      {
        path: '/reset-password',
        element: <ResetPasswordPage />
      }
    ]
  },
  // PROFILE
  {
    errorElement: <ErrorBoundary />,
    element: (
      <ProtectedRoute>
        <ProfileLayout />
      </ProtectedRoute>
    ),
    path: '/profile',
    children: [
      {
        path: '/profile',
        element: <ProfilePage />
      },
      {
        path: 'transaction',
        element: <TransactionPage />
      },
      {
        path: ':profileCategory',
        element: <ProfilePage />
      },
      {
        path: 'favorite',
        element: <FavoritePage />
      },
      {
        path: 'favorite/:favoriteCategory',
        element: <FavoritePage />
      },
      {
        path: 'membership',
        element: <MembershipPage />
      },
      {
        path: 'membership/:membershipCategory',
        element: <MembershipPage />
      },
      {
        path: 'search-agent',
        element: <SearchAgentPage />
      },
      {
        path: 'search-agent/:searchAgentCategory',
        element: <SearchAgentPage />
      },
      {
        path: 'message',
        element: <MessagePage />
      },
      {
        path: 'message/request',
        element: <MessageRequestPage />
      },
      {
        path: 'events',
        element: <ProfileEvent />
      },
      {
        path: 'webinars',
        element: <ProfileWebinar />
      }
    ]
  },
  // ONBOARDING
  {
    errorElement: <ErrorBoundary />,
    element: (
      <ProtectedRoute>
        <ProtectedBasicLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/profile/transaction/:transactionId/detail-transaction',
        element: <XenditTransactionPage />
      },
      {
        path: '/profile/membership/upgrade-package',
        element: <UpgradeMembershipPage />
      },
      {
        path: '/profile/membership/renew-package',
        element: <RenewMembershipPage />
      },
      {
        path: '/onboarding',
        element: <OnboardingPage />
      },
      {
        path: '/:accountType/buy-package',
        element: <PackagingPage />
      },
      {
        path: '/profile/events/detail/:id',
        element: <ProfileEventDetail />
      },
      {
        path: '/profile/webinars/detail/:id',
        element: <ProfileWebinarDetail />
      },
      {
        path: '/checkout-extra-bundle',
        element: <CheckoutExtraBundle />
      },
      {
        path: '/checkout/credit-card',
        element: <CreditCardPaymentPage />
      },
      {
        path: '/valuation-tool/checkout',
        element: <ValuationToolsCheckout />
      }
    ]
  },
  {
    errorElement: <ErrorBoundary />,
    element: (
      <ProtectedRoute>
        <BlankLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/valuation-tool-calculate',
        element: <ValuationToolPage />
      }
    ]
  },

  // BUY SELL LAYOUT
  {
    errorElement: <ErrorBoundary />,
    element: (
      <ProtectedRoute>
        <BuySellLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/buyer/listing/buy-company',
        element: <BuyCompanyV2Page />
      },
      {
        path: '/advisor/listing/buy-company',
        element: <BuyCompanyV2Page />
      },
      {
        path: '/seller/listing/sell-company',
        element: <SellCompanyPage />
      },
      {
        path: '/advisor/listings/advisor-profile',
        element: <CreateAdvisorProfilePageV2 />
      },
      {
        path: '/advisor/listing/sell-company',
        element: <SellCompanyPage />
      },
      {
        path: '/subscription',
        element: <SubscriptionFormPage />
      },
      {
        path: '/sponsorship',
        element: <Sponsorship />
      }
    ]
  },
  {
    errorElement: <ErrorBoundary />,
    path: '/profile/transaction/invoice',
    element: (
      <ProtectedRoute>
        <Suspense
          fallback={
            <div
              className="
        h-screen w-screen flex justify-center items-center">
              <Spin size="large" spinning={true}></Spin>
            </div>
          }>
          <TransactionInvoicePage />
        </Suspense>
      </ProtectedRoute>
    )
  },
  {
    errorElement: <ErrorBoundary />,
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/404',
        element: <NotFound />
      },
      {
        path: '/',
        element: <HomePage />
      },
      {
         path: '/terms-and-conditions',
         element: <HomePage />
      },
      {
         path: '/privacy-policy',
         element: <HomePage />
      },
      // {
      //   path: '/valuation',
      //   element: <ValuationPage />
      // },
      {
        path: '/pricing',
        element: <PricingPage />
      },
      {
        path: '/pricing/:subscriptionType',
        element: <PricingPage />
      },
      {
        path: '/buyer',
        element: <WantedCompaniesPage />
      },
      {
        path: '/buyer/detail/:id',
        element: <CompanyForSaleDetailPage />
      },
      {
        path: '/advisor/listing/buy-company/verification',
        element: <ListingVerificationPage />
      },
      {
        path: '/buyer/listing/buy-company/verification',
        element: <ListingVerificationPage />
      },
      {
        path: '/investor',
        element: <WantedCompaniesPage />
      },
      {
        path: '/franchise',
        element: <WantedCompaniesPage />
      },
      {
        path: '/seller',
        element: <CompanyForSalePage />
      },
      {
        path: '/seller/detail/:id',
        element: <WantedCompanyDetailPage />
      },
      {
        path: '/seller/listing/sell-company/verification',
        element: <ListingVerificationPage />
      },
      {
        path: '/advisor/listing/sell-company/verification',
        element: <ListingVerificationPage />
      },
      {
        path: '/events',
        element: <EventPage />
      },
      {
        path: '/events/detail/:id',
        element: <EventDetailPage />
      },
      {
        path: '/events/checkout/:id',
        element: <EventCheckoutPage />
      },
      {
        path: '/webinars/checkout/:id',
        element: <EventCheckoutPage />
      },
      {
        path: '/blog',
        element: <BlogPage />
      },
      {
        path: '/blog/:id/detail',
        element: <BlogDetailPage />
      },
      {
        path: '/knowledge-base',
        element: <KnowledgeBasegPage />
      },
      {
        path: '/knowledge-base/detail/:id',
        element: <DetailKnowledgeBasegPage />
      },
      {
        path: '/valuation-tool',
        element: <LadingPageValuationTool />
      },
      {
        path: '/about-tomazz',
        element: <AboutUsPage />
      },
      {
        path: '/support',
        element: <SupportPage />
      },
      {
        path: '/advisor',
        element: <AdvisorCatalogPage />
      },
      {
        path: '/advisor/detail/:id',
        element: <AdvisorDetailPage />
      },
      {
        path: '/advisor/listings/verification',
        element: <ListingVerificationPage />
      },
      {
        path: '/subscription/payment-success',
        element: <SubscriptionPaymentSuccessPage />
      },
      {
        path: '/:accountType/packaging/payment-success',
        element: <PackagingPaymentSuccess />
      }
    ]
  }
])

const Router = () => {
  return <RouterProvider router={router} />
}

export default Router
